import React, { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { createSupabaseWithAuth } from '../../../../Components/Utils/SupabaseConfig';
import { useSession } from '../../../../Components/Utils/SessionContext';
import SearchableSelectUI from './SearchableSelectUI';

const TalentSearchSelect = ({
  label = "Select Talent",
  placeholder = "Search for a talent...",
  selectedTalent,
  onTalentSelect,
  onClear,
  disabled = false,
  clearable = true,
  searchDelay = 300,
  limit = 10
}) => {
  const { user } = useSession();
  const [searchTerm, setSearchTerm] = useState(selectedTalent?.name || '');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Update search term when selectedTalent changes
  useEffect(() => {
    if (selectedTalent) {
      setSearchTerm(selectedTalent.name || '');
    }
  }, [selectedTalent]);

  // Function to fetch talent suggestions from Supabase
  const fetchTalentSuggestions = async (term) => {
    if (!term.trim() || term.length < 2) {
      setResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      // Check for user session
      if (!user || !user.access_token) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Create Supabase client with auth
      const supabaseWithAuth = createSupabaseWithAuth(user.access_token);
      
      // Make the search query
      const { data, error } = await supabaseWithAuth
        .from('talent')
        .select('talent_id, fname, lname, email, roleCategory, status')
        .eq('uid', user.id) // Filter by user ID
        .or(`fname.ilike.%${term}%,lname.ilike.%${term}%,email.ilike.%${term}%`)
        .limit(limit);
      
      if (error) {
        console.error('Talent search error:', error);
        setResults([]);
        return;
      }
      
      if (!data || data.length === 0) {
        setResults([]);
        return;
      }
      
      // Format the results
      const formattedResults = data.map(item => ({
        talent_id: item.talent_id,
        name: `${item.fname} ${item.lname}`.trim(),
        email: item.email,
        roleCategory: item.roleCategory,
        status: item.status
      }));
      
      setResults(formattedResults);
    } catch (error) {
      console.error('Error searching for talent:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Create debounced search function
  const debouncedFetchTalents = useMemo(
    () => debounce(fetchTalentSuggestions, searchDelay),
    [user, searchDelay]
  );
  
  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length >= 2) {
      debouncedFetchTalents(term);
    } else {
      setResults([]);
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFetchTalents.cancel();
    };
  }, [debouncedFetchTalents]);

  // Handle talent selection
  const handleTalentSelect = (talent) => {
    onTalentSelect(talent);
    setSearchTerm(talent.name);
    setResults([]);
  };

  // Handle clear
  const handleClear = () => {
    onClear();
    setSearchTerm('');
    setResults([]);
  };

  return (
    <SearchableSelectUI
      id="talentSearch"
      label={label}
      placeholder={placeholder}
      searchTerm={searchTerm}
      onSearchChange={handleSearchChange}
      results={results}
      isSearching={isSearching}
      selectedItem={selectedTalent}
      onItemSelect={handleTalentSelect}
      onClear={handleClear}
      disabled={disabled}
      clearable={clearable}
      renderItem={(talent) => (
        <div className="cursor-pointer">
          <div className="flex justify-between items-center">
            <div className="font-medium">{talent.name}</div>
            <span className="text-xs text-gray-500 font-medium pr-8">{talent.status}</span>
          </div>
          {talent.email && <div className="text-sm text-gray-600">{talent.email}</div>}
          {talent.roleCategory && talent.roleCategory.subCategory && (
            <div className="text-xs text-gray-500 mt-1">
              <span className="font-medium">{talent.roleCategory.subCategory}</span>
              {talent.roleCategory.mainCategory && (
                <span> - {talent.roleCategory.mainCategory}</span>
              )}
              {talent.roleCategory.altSubcategory && (
                <span className="italic ml-1">({talent.roleCategory.altSubcategory})</span>
              )}
            </div>
          )}
        </div>
      )}
      renderSelectedItem={(talent) => (
        <div>
          <div className="flex justify-between items-center">
            <div className="font-medium">{talent.name}</div>
            <span className="text-xs text-gray-500 font-medium pr-8">{talent.status}</span>
          </div>
          {talent.email && <div className="text-sm text-gray-600">{talent.email}</div>}
          {talent.roleCategory && talent.roleCategory.subCategory && (
            <div className="text-xs text-gray-500 mt-1">
              <span className="font-medium">{talent.roleCategory.subCategory}</span>
              {talent.roleCategory.mainCategory && (
                <span> - {talent.roleCategory.mainCategory}</span>
              )}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default TalentSearchSelect;
